'use client';
import React, { useState, useMemo, useRef } from 'react';
import AppLayout from '@/components/AppLayout';
import Link from 'next/link';
import { ArrowLeft, Plus, Upload, Map, List, Search, Download, Send, CheckCircle, AlertTriangle, MapPin, Building2, Warehouse, X, Eye, CheckSquare, Square, BarChart2, TrendingUp, FileText, Shield, Phone, User, Landmark, Flag, AlertCircle, Info, ChevronRight,  } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,  } from 'recharts';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type PropertyClass =
  | 'Residential' | 'Commercial' | 'Industrial' | 'Vacant Land' | 'Farmland' |'Public-Land Improvement'| 'Mixed Use' | 'Institutional' | 'Special Economic Zone' |'Heritage/Protected' | 'Concession Land';

type RegistrationStatus =
  | 'draft' | 'captured' | 'geolocated' | 'documents_pending' | 'under_review'
  | 'classified' | 'verified' | 'flagged' | 'rejected' | 'sent_to_stage2';

type OwnershipType =
  | 'Individual' | 'Joint Ownership' | 'Corporate Entity' | 'Government Agency' |'NGO/Non-Profit' | 'Trust/Estate' | 'Foreign National' | 'Concession Holder';

type TenureType =
  | 'Freehold' | 'Leasehold' | 'Customary' | 'Government Lease' |'Concession' | 'Disputed' | 'Unknown';

type County =
  | 'Montserrado' | 'Margibi' | 'Bong' | 'Nimba' | 'Lofa' | 'Grand Bassa'
  | 'Sinoe'| 'Maryland' | 'Grand Cape Mount' | 'Grand Gedeh' | 'Rivercess' |'Gbarpolu' | 'River Gee' | 'Grand Kru' | 'Bomi';

type GeoSource = 'GPS Field Survey' | 'Drone Survey' | 'Satellite Imagery' | 'Manual Entry' | 'LIDAR' | 'Cadastral Map';

interface OwnerRecord {
  fullName: string;
  nationalId: string;
  taxId: string;
  ownershipType: OwnershipType;
  phone: string;
  email: string;
  postalAddress: string;
  nationality: string;
  dateOfBirth: string;
  gender: 'Male' | 'Female' | 'Other' | '';
  occupation: string;
  coOwners: string;
}

interface LocationRecord {
  county: County;
  district: string;
  city: string;
  community: string;
  streetAddress: string;
  landmark: string;
  postalCode: string;
  lat: string;
  lng: string;
  elevation: string;
  geoSource: GeoSource;
  geoAccuracy: string;
  geoDate: string;
  geoOfficer: string;
}

interface PropertyDetails {
  propertyClass: PropertyClass;
  subClass: string;
  tenureType: TenureType;
  areaSqm: string;
  buildingAreaSqm: string;
  floors: string;
  yearBuilt: string;
  constructionType: string;
  roofType: string;
  wallMaterial: string;
  utilities: string[];
  accessRoad: string;
  boundaryType: string;
  irrigated: boolean;
  cropType: string;
}

interface LegalRecord {
  titleDeedNumber: string;
  titleDeedDate: string;
  registrationNumber: string;
  registrationDate: string;
  surveyPlanNumber: string;
  surveyDate: string;
  courtOrderNumber: string;
  encumbrances: string;
  mortgageStatus: string;
  disputeStatus: 'None' | 'Active' | 'Resolved' | 'Pending Court';
  disputeDetails: string;
  previousOwner: string;
  transferDate: string;
  inheritanceCase: boolean;
}

interface ValuationEstimate {
  estimatedMarketValue: string;
  estimatedRentalValue: string;
  lastValuationDate: string;
  valuationSource: string;
  improvementValue: string;
  landValue: string;
}

interface DocumentRecord {
  titleDeed: boolean;
  surveyPlan: boolean;
  nationalId: boolean;
  taxClearance: boolean;
  buildingPermit: boolean;
  occupancyCert: boolean;
  courtOrder: boolean;
  inheritanceDocs: boolean;
  photos: boolean;
  droneFootage: boolean;
}

interface AuditEntry {
  timestamp: string;
  action: string;
  officer: string;
  details: string;
  status: 'info' | 'success' | 'warning' | 'error';
}

interface PropertyRecord {
  id: string;
  parcelId: string;
  registrationRef: string;
  owner: OwnerRecord;
  location: LocationRecord;
  property: PropertyDetails;
  legal: LegalRecord;
  valuation: ValuationEstimate;
  documents: DocumentRecord;
  status: RegistrationStatus;
  capturedBy: string;
  capturedDate: string;
  lastModified: string;
  assignedOfficer: string;
  priority: 'Normal' | 'High' | 'Urgent';
  sentToStage2: boolean;
  auditTrail: AuditEntry[];
  notes: string;
  internalFlags: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const COUNTIES: County[] = [
  'Montserrado', 'Margibi', 'Bong', 'Nimba', 'Lofa', 'Grand Bassa',
  'Sinoe', 'Maryland', 'Grand Cape Mount', 'Grand Gedeh', 'Rivercess',
  'Gbarpolu', 'River Gee', 'Grand Kru', 'Bomi',
];

const PROPERTY_CLASSES: PropertyClass[] = [
  'Residential', 'Commercial', 'Industrial', 'Vacant Land', 'Farmland',
  'Public-Land Improvement', 'Mixed Use', 'Institutional', 'Special Economic Zone',
  'Heritage/Protected', 'Concession Land',
];

const CLASS_COLORS: Record<PropertyClass, string> = {
  'Residential': '#60a5fa',
  'Commercial': '#f59e0b',
  'Industrial': '#f97316',
  'Vacant Land': '#34d399',
  'Farmland': '#a3e635',
  'Public-Land Improvement': '#a78bfa',
  'Mixed Use': '#fb7185',
  'Institutional': '#38bdf8',
  'Special Economic Zone': '#e879f9',
  'Heritage/Protected': '#fcd34d',
  'Concession Land': '#6ee7b7',
};

const STATUS_CONFIG: Record<RegistrationStatus, { label: string; color: string; bg: string }> = {
  draft:              { label: 'Draft',              color: '#6b7280', bg: 'rgba(107,114,128,0.12)' },
  captured:           { label: 'Captured',           color: '#94a3b8', bg: 'rgba(148,163,184,0.12)' },
  geolocated:         { label: 'Geolocated',         color: '#60a5fa', bg: 'rgba(96,165,250,0.12)' },
  documents_pending:  { label: 'Docs Pending',       color: '#fb923c', bg: 'rgba(251,146,60,0.12)' },
  under_review:       { label: 'Under Review',       color: '#a78bfa', bg: 'rgba(167,139,250,0.12)' },
  classified:         { label: 'Classified',         color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  verified:           { label: 'Verified',           color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  flagged:            { label: 'Flagged',            color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  rejected:           { label: 'Rejected',           color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
  sent_to_stage2:     { label: 'Sent → Stage 2',     color: '#6ee7b7', bg: 'rgba(110,231,183,0.12)' },
};

const OFFICERS = ['A. Kamara', 'B. Togba', 'C. Flomo', 'D. Kpaan', 'E. Mulbah', 'F. Bility', 'G. Weah', 'T. Freeman', 'J. Jallah', 'M. Kollie'];

// ─── Mock Data ────────────────────────────────────────────────────────────────

const makeAudit = (entries: Array<{ action: string; officer: string; details: string; status: AuditEntry['status']; daysAgo: number }>): AuditEntry[] =>
  entries.map(e => {
    const d = new Date('2026-07-08');
    d.setDate(d.getDate() - e.daysAgo);
    return { timestamp: d.toISOString().replace('T', ' ').slice(0, 16), action: e.action, officer: e.officer, details: e.details, status: e.status };
  });

const initialProperties: PropertyRecord[] = [
  {
    id: 'P001', parcelId: 'LR-MON-2024-0001', registrationRef: 'REG-2024-MON-00421',
    owner: { fullName: 'James K. Kollie', nationalId: 'LR-NID-0042198', taxId: 'LRA-TIN-20240421', ownershipType: 'Individual', phone: '+231-770-421-001', email: 'jkollie@email.lr', postalAddress: 'P.O. Box 421, Monrovia', nationality: 'Liberian', dateOfBirth: '1975-03-14', gender: 'Male', occupation: 'Business Owner', coOwners: '' },
    location: { county: 'Montserrado', district: 'Greater Monrovia', city: 'Monrovia', community: 'Central Business District', streetAddress: '12 Broad Street', landmark: 'Near Executive Mansion', postalCode: '1000', lat: '6.3005', lng: '-10.7969', elevation: '28m', geoSource: 'GPS Field Survey', geoAccuracy: '±2m', geoDate: '2024-11-03', geoOfficer: 'A. Kamara' },
    property: { propertyClass: 'Commercial', subClass: 'Retail/Office', tenureType: 'Freehold', areaSqm: '420', buildingAreaSqm: '380', floors: '3', yearBuilt: '2008', constructionType: 'Reinforced Concrete', roofType: 'Flat/Concrete', wallMaterial: 'Concrete Block', utilities: ['Electricity', 'Water', 'Internet'], accessRoad: 'Paved', boundaryType: 'Concrete Wall', irrigated: false, cropType: '' },
    legal: { titleDeedNumber: 'TD-MON-2008-0421', titleDeedDate: '2008-06-15', registrationNumber: 'LR-REG-2008-0421', registrationDate: '2008-07-01', surveyPlanNumber: 'SP-MON-2008-0421', surveyDate: '2008-05-20', courtOrderNumber: '', encumbrances: 'None', mortgageStatus: 'Clear', disputeStatus: 'None', disputeDetails: '', previousOwner: 'Monrovia City Corp.', transferDate: '2008-06-15', inheritanceCase: false },
    valuation: { estimatedMarketValue: '185000', estimatedRentalValue: '2400', lastValuationDate: '2023-01-15', valuationSource: 'LRA Field Assessment', improvementValue: '120000', landValue: '65000' },
    documents: { titleDeed: true, surveyPlan: true, nationalId: true, taxClearance: true, buildingPermit: true, occupancyCert: true, courtOrder: false, inheritanceDocs: false, photos: true, droneFootage: false },
    status: 'verified', capturedBy: 'A. Kamara', capturedDate: '2024-11-03', lastModified: '2024-11-15', assignedOfficer: 'J. Jallah', priority: 'Normal', sentToStage2: false, notes: 'Corner lot, active business. All documents verified.',
    internalFlags: [],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'A. Kamara', details: 'Initial field capture via GPS survey', status: 'info', daysAgo: 65 },
      { action: 'Documents Uploaded', officer: 'A. Kamara', details: 'Title deed, survey plan, national ID uploaded', status: 'success', daysAgo: 63 },
      { action: 'Classification Set', officer: 'J. Jallah', details: 'Classified as Commercial — Retail/Office', status: 'info', daysAgo: 58 },
      { action: 'Ownership Verified', officer: 'J. Jallah', details: 'Owner identity confirmed against National ID Registry', status: 'success', daysAgo: 55 },
      { action: 'Status: Verified', officer: 'J. Jallah', details: 'All checks passed. Ready for Stage 2.', status: 'success', daysAgo: 50 },
    ]),
  },
  {
    id: 'P002', parcelId: 'LR-MON-2024-0002', registrationRef: 'REG-2024-MON-00388',
    owner: { fullName: 'Mary T. Freeman', nationalId: 'LR-NID-0038812', taxId: 'LRA-TIN-20240388', ownershipType: 'Individual', phone: '+231-880-388-002', email: 'mfreeman@email.lr', postalAddress: '45 Tubman Blvd, Sinkor', nationality: 'Liberian', dateOfBirth: '1982-07-22', gender: 'Female', occupation: 'Civil Servant', coOwners: 'John T. Freeman (Spouse)' },
    location: { county: 'Montserrado', district: 'Sinkor', city: 'Monrovia', community: 'Sinkor', streetAddress: '45 Tubman Boulevard', landmark: 'Near Sinkor Market', postalCode: '1000', lat: '6.3120', lng: '-10.8010', elevation: '22m', geoSource: 'GPS Field Survey', geoAccuracy: '±3m', geoDate: '2024-11-05', geoOfficer: 'B. Togba' },
    property: { propertyClass: 'Residential', subClass: 'Single-Family Dwelling', tenureType: 'Freehold', areaSqm: '280', buildingAreaSqm: '210', floors: '1', yearBuilt: '1998', constructionType: 'Concrete Block', roofType: 'Zinc/Metal', wallMaterial: 'Concrete Block', utilities: ['Electricity', 'Water'], accessRoad: 'Paved', boundaryType: 'Iron Fence', irrigated: false, cropType: '' },
    legal: { titleDeedNumber: 'TD-MON-1998-0388', titleDeedDate: '1998-03-10', registrationNumber: 'LR-REG-1998-0388', registrationDate: '1998-04-01', surveyPlanNumber: 'SP-MON-1998-0388', surveyDate: '1998-02-15', courtOrderNumber: '', encumbrances: 'None', mortgageStatus: 'Active Mortgage — LIB Bank', disputeStatus: 'None', disputeDetails: '', previousOwner: 'Estate of T. Freeman', transferDate: '1998-03-10', inheritanceCase: true },
    valuation: { estimatedMarketValue: '62000', estimatedRentalValue: '450', lastValuationDate: '2023-03-20', valuationSource: 'LRA Field Assessment', improvementValue: '38000', landValue: '24000' },
    documents: { titleDeed: true, surveyPlan: true, nationalId: true, taxClearance: false, buildingPermit: false, occupancyCert: false, courtOrder: false, inheritanceDocs: true, photos: true, droneFootage: false },
    status: 'documents_pending', capturedBy: 'B. Togba', capturedDate: '2024-11-05', lastModified: '2024-11-20', assignedOfficer: 'M. Kollie', priority: 'Normal', sentToStage2: false, notes: 'Tax clearance and building permit outstanding.',
    internalFlags: ['Missing tax clearance', 'Inheritance case — verify court order'],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'B. Togba', details: 'Field capture, GPS coordinates recorded', status: 'info', daysAgo: 63 },
      { action: 'Documents Partially Uploaded', officer: 'B. Togba', details: 'Title deed and national ID uploaded. Tax clearance missing.', status: 'warning', daysAgo: 60 },
      { action: 'Flag Added', officer: 'M. Kollie', details: 'Inheritance case flagged — court order required', status: 'warning', daysAgo: 55 },
      { action: 'Status: Documents Pending', officer: 'M. Kollie', details: 'Awaiting tax clearance and building permit', status: 'warning', daysAgo: 48 },
    ]),
  },
  {
    id: 'P003', parcelId: 'LR-MAR-2024-0001', registrationRef: 'REG-2024-MAR-00112',
    owner: { fullName: 'Emmanuel D. Sumo', nationalId: 'LR-NID-0011245', taxId: 'LRA-TIN-20240112', ownershipType: 'Individual', phone: '+231-770-112-003', email: 'esumo@agri.lr', postalAddress: 'Old Road, Kakata, Margibi', nationality: 'Liberian', dateOfBirth: '1965-11-08', gender: 'Male', occupation: 'Farmer', coOwners: '' },
    location: { county: 'Margibi', district: 'Kakata', city: 'Kakata', community: 'Kakata Rural', streetAddress: 'Old Road, Km 4', landmark: 'Near Kakata Agricultural Station', postalCode: '2000', lat: '6.5310', lng: '-10.3540', elevation: '85m', geoSource: 'Drone Survey', geoAccuracy: '±1m', geoDate: '2024-11-08', geoOfficer: 'C. Flomo' },
    property: { propertyClass: 'Farmland', subClass: 'Rubber Plantation', tenureType: 'Customary', areaSqm: '12400', buildingAreaSqm: '0', floors: '0', yearBuilt: '', constructionType: 'N/A', roofType: 'N/A', wallMaterial: 'N/A', utilities: ['Water'], accessRoad: 'Gravel', boundaryType: 'Natural Boundary', irrigated: true, cropType: 'Rubber' },
    legal: { titleDeedNumber: '', titleDeedDate: '', registrationNumber: 'LR-CUST-2010-0112', registrationDate: '2010-01-15', surveyPlanNumber: 'SP-MAR-2010-0112', surveyDate: '2010-01-10', courtOrderNumber: '', encumbrances: 'None', mortgageStatus: 'Clear', disputeStatus: 'None', disputeDetails: '', previousOwner: 'Community Land', transferDate: '2010-01-15', inheritanceCase: false },
    valuation: { estimatedMarketValue: '28000', estimatedRentalValue: '0', lastValuationDate: '2022-06-10', valuationSource: 'Ministry of Agriculture', improvementValue: '8000', landValue: '20000' },
    documents: { titleDeed: false, surveyPlan: true, nationalId: true, taxClearance: true, buildingPermit: false, occupancyCert: false, courtOrder: false, inheritanceDocs: false, photos: true, droneFootage: true },
    status: 'geolocated', capturedBy: 'C. Flomo', capturedDate: '2024-11-08', lastModified: '2024-11-22', assignedOfficer: 'C. Flomo', priority: 'Normal', sentToStage2: false, notes: 'Active rubber plantation. Customary tenure — no formal title deed.',
    internalFlags: ['Customary tenure — no title deed'],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'C. Flomo', details: 'Drone survey completed, boundary mapped', status: 'info', daysAgo: 60 },
      { action: 'Geolocation Confirmed', officer: 'C. Flomo', details: 'Drone imagery processed, coordinates verified', status: 'success', daysAgo: 58 },
      { action: 'Flag Added', officer: 'C. Flomo', details: 'Customary tenure — formal title deed not available', status: 'warning', daysAgo: 55 },
    ]),
  },
  {
    id: 'P004', parcelId: 'LR-BON-2024-0001', registrationRef: 'REG-2024-BON-00209',
    owner: { fullName: 'Agnes W. Pewee', nationalId: 'LR-NID-0020934', taxId: 'LRA-TIN-20240209', ownershipType: 'Individual', phone: '+231-880-209-004', email: 'apewee@bong.lr', postalAddress: 'Gbarnga City Center, Bong', nationality: 'Liberian', dateOfBirth: '1970-05-30', gender: 'Female', occupation: 'Trader', coOwners: '' },
    location: { county: 'Bong', district: 'Gbarnga', city: 'Gbarnga', community: 'City Center', streetAddress: 'Main Street, Gbarnga', landmark: 'Near Bong County Courthouse', postalCode: '3000', lat: '6.9940', lng: '-9.4720', elevation: '210m', geoSource: 'GPS Field Survey', geoAccuracy: '±2m', geoDate: '2024-11-10', geoOfficer: 'D. Kpaan' },
    property: { propertyClass: 'Commercial', subClass: 'Multi-Story Commercial', tenureType: 'Freehold', areaSqm: '650', buildingAreaSqm: '580', floors: '4', yearBuilt: '2015', constructionType: 'Reinforced Concrete', roofType: 'Flat/Concrete', wallMaterial: 'Concrete Block', utilities: ['Electricity', 'Water', 'Generator'], accessRoad: 'Paved', boundaryType: 'Concrete Wall', irrigated: false, cropType: '' },
    legal: { titleDeedNumber: 'TD-BON-2015-0209', titleDeedDate: '2015-08-20', registrationNumber: 'LR-REG-2015-0209', registrationDate: '2015-09-01', surveyPlanNumber: 'SP-BON-2015-0209', surveyDate: '2015-07-15', courtOrderNumber: '', encumbrances: 'None', mortgageStatus: 'Clear', disputeStatus: 'None', disputeDetails: '', previousOwner: 'Bong County Dev. Corp.', transferDate: '2015-08-20', inheritanceCase: false },
    valuation: { estimatedMarketValue: '210000', estimatedRentalValue: '1800', lastValuationDate: '2023-05-10', valuationSource: 'LRA Field Assessment', improvementValue: '150000', landValue: '60000' },
    documents: { titleDeed: true, surveyPlan: true, nationalId: true, taxClearance: true, buildingPermit: true, occupancyCert: true, courtOrder: false, inheritanceDocs: false, photos: true, droneFootage: false },
    status: 'verified', capturedBy: 'D. Kpaan', capturedDate: '2024-11-10', lastModified: '2024-11-25', assignedOfficer: 'J. Jallah', priority: 'High', sentToStage2: false, notes: 'Multi-story commercial building. All documents complete.',
    internalFlags: [],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'D. Kpaan', details: 'GPS survey, all coordinates recorded', status: 'info', daysAgo: 58 },
      { action: 'Documents Uploaded', officer: 'D. Kpaan', details: 'Full document set uploaded', status: 'success', daysAgo: 55 },
      { action: 'Classification Set', officer: 'J. Jallah', details: 'Commercial — Multi-Story', status: 'info', daysAgo: 50 },
      { action: 'Status: Verified', officer: 'J. Jallah', details: 'All checks passed', status: 'success', daysAgo: 43 },
    ]),
  },
  {
    id: 'P005', parcelId: 'LR-NIM-2024-0001', registrationRef: 'REG-2024-NIM-00074',
    owner: { fullName: 'Thomas B. Ziah', nationalId: 'LR-NID-0007412', taxId: '', ownershipType: 'Individual', phone: '+231-770-074-005', email: '', postalAddress: 'Sanniquellie Main St, Nimba', nationality: 'Liberian', dateOfBirth: '1988-09-14', gender: 'Male', occupation: 'Unknown', coOwners: '' },
    location: { county: 'Nimba', district: 'Sanniquellie', city: 'Sanniquellie', community: 'Main Town', streetAddress: 'Main Street', landmark: 'Near Nimba County Hospital', postalCode: '4000', lat: '7.3600', lng: '-8.7060', elevation: '480m', geoSource: 'Manual Entry', geoAccuracy: '±50m', geoDate: '2024-11-12', geoOfficer: 'E. Mulbah' },
    property: { propertyClass: 'Residential', subClass: 'Single-Family Dwelling', tenureType: 'Unknown', areaSqm: '190', buildingAreaSqm: '150', floors: '1', yearBuilt: '', constructionType: 'Mud/Brick', roofType: 'Zinc/Metal', wallMaterial: 'Mud Brick', utilities: [], accessRoad: 'Dirt', boundaryType: 'Natural Boundary', irrigated: false, cropType: '' },
    legal: { titleDeedNumber: '', titleDeedDate: '', registrationNumber: '', registrationDate: '', surveyPlanNumber: '', surveyDate: '', courtOrderNumber: '', encumbrances: 'Unknown', mortgageStatus: 'Unknown', disputeStatus: 'Pending Court', disputeDetails: 'Boundary dispute with adjacent property owner', previousOwner: 'Unknown', transferDate: '', inheritanceCase: false },
    valuation: { estimatedMarketValue: '8500', estimatedRentalValue: '0', lastValuationDate: '', valuationSource: '', improvementValue: '3500', landValue: '5000' },
    documents: { titleDeed: false, surveyPlan: false, nationalId: true, taxClearance: false, buildingPermit: false, occupancyCert: false, courtOrder: false, inheritanceDocs: false, photos: true, droneFootage: false },
    status: 'flagged', capturedBy: 'E. Mulbah', capturedDate: '2024-11-12', lastModified: '2024-11-28', assignedOfficer: 'E. Mulbah', priority: 'High', sentToStage2: false, notes: 'Pending GPS verification. Boundary dispute active. No formal documents.',
    internalFlags: ['No title deed', 'Active boundary dispute', 'GPS accuracy low — manual entry only', 'No Tax ID on record'],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'E. Mulbah', details: 'Manual entry — GPS not available at site', status: 'warning', daysAgo: 56 },
      { action: 'Flag Added', officer: 'E. Mulbah', details: 'No formal documents. Boundary dispute reported by neighbor.', status: 'error', daysAgo: 54 },
      { action: 'Dispute Logged', officer: 'E. Mulbah', details: 'Boundary dispute with adjacent property — court case pending', status: 'error', daysAgo: 50 },
      { action: 'Status: Flagged', officer: 'E. Mulbah', details: 'Flagged pending dispute resolution and document collection', status: 'error', daysAgo: 40 },
    ]),
  },
  {
    id: 'P006', parcelId: 'LR-MON-2024-0003', registrationRef: 'REG-2024-MON-00501',
    owner: { fullName: 'Fatima A. Konneh', nationalId: 'LR-NID-0050134', taxId: 'LRA-TIN-20240501', ownershipType: 'Individual', phone: '+231-880-501-006', email: 'fkonneh@email.lr', postalAddress: 'Paynesville Red Light, Monrovia', nationality: 'Liberian', dateOfBirth: '1979-12-01', gender: 'Female', occupation: 'Business Owner', coOwners: '' },
    location: { county: 'Montserrado', district: 'Paynesville', city: 'Paynesville', community: 'Red Light', streetAddress: 'Red Light Market Area', landmark: 'Near Red Light Bus Terminal', postalCode: '1001', lat: '6.3280', lng: '-10.7540', elevation: '35m', geoSource: 'GPS Field Survey', geoAccuracy: '±2m', geoDate: '2024-11-14', geoOfficer: 'A. Kamara' },
    property: { propertyClass: 'Mixed Use', subClass: 'Ground Floor Commercial / Upper Residential', tenureType: 'Freehold', areaSqm: '510', buildingAreaSqm: '460', floors: '2', yearBuilt: '2012', constructionType: 'Concrete Block', roofType: 'Zinc/Metal', wallMaterial: 'Concrete Block', utilities: ['Electricity', 'Water'], accessRoad: 'Paved', boundaryType: 'Concrete Wall', irrigated: false, cropType: '' },
    legal: { titleDeedNumber: 'TD-MON-2012-0501', titleDeedDate: '2012-04-18', registrationNumber: 'LR-REG-2012-0501', registrationDate: '2012-05-01', surveyPlanNumber: 'SP-MON-2012-0501', surveyDate: '2012-03-20', courtOrderNumber: '', encumbrances: 'None', mortgageStatus: 'Clear', disputeStatus: 'None', disputeDetails: '', previousOwner: 'Paynesville City Corp.', transferDate: '2012-04-18', inheritanceCase: false },
    valuation: { estimatedMarketValue: '95000', estimatedRentalValue: '1200', lastValuationDate: '2023-02-28', valuationSource: 'LRA Field Assessment', improvementValue: '60000', landValue: '35000' },
    documents: { titleDeed: true, surveyPlan: true, nationalId: true, taxClearance: true, buildingPermit: true, occupancyCert: false, courtOrder: false, inheritanceDocs: false, photos: true, droneFootage: false },
    status: 'classified', capturedBy: 'A. Kamara', capturedDate: '2024-11-14', lastModified: '2024-11-30', assignedOfficer: 'M. Kollie', priority: 'Normal', sentToStage2: false, notes: 'Ground floor commercial, upper residential. Occupancy cert pending.',
    internalFlags: ['Occupancy certificate outstanding'],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'A. Kamara', details: 'GPS survey completed', status: 'info', daysAgo: 54 },
      { action: 'Documents Uploaded', officer: 'A. Kamara', details: 'Most documents uploaded. Occupancy cert missing.', status: 'warning', daysAgo: 52 },
      { action: 'Classification Set', officer: 'M. Kollie', details: 'Mixed Use — Ground Commercial / Upper Residential', status: 'info', daysAgo: 48 },
    ]),
  },
  {
    id: 'P007', parcelId: 'LR-GBA-2024-0001', registrationRef: 'REG-2024-GBA-00143',
    owner: { fullName: 'Ruth M. Nagbe', nationalId: 'LR-NID-0014389', taxId: 'LRA-TIN-20240143', ownershipType: 'Corporate Entity', phone: '+231-770-143-007', email: 'rnagbe@gbindustrial.lr', postalAddress: 'Buchanan Harbor Rd, Grand Bassa', nationality: 'Liberian', dateOfBirth: '1968-04-25', gender: 'Female', occupation: 'Company Director', coOwners: 'Grand Bassa Industrial Corp.' },
    location: { county: 'Grand Bassa', district: 'Buchanan', city: 'Buchanan', community: 'Harbor Industrial Zone', streetAddress: 'Harbor Road, Industrial Zone', landmark: 'Near Buchanan Port', postalCode: '5000', lat: '5.8810', lng: '-10.0460', elevation: '12m', geoSource: 'Drone Survey', geoAccuracy: '±1m', geoDate: '2024-11-17', geoOfficer: 'G. Weah' },
    property: { propertyClass: 'Industrial', subClass: 'Warehouse Complex', tenureType: 'Leasehold', areaSqm: '8900', buildingAreaSqm: '6200', floors: '1', yearBuilt: '2018', constructionType: 'Steel Frame', roofType: 'Metal Sheet', wallMaterial: 'Steel/Metal', utilities: ['Electricity', 'Water', 'Generator', 'Internet'], accessRoad: 'Paved', boundaryType: 'Security Fence', irrigated: false, cropType: '' },
    legal: { titleDeedNumber: '', titleDeedDate: '', registrationNumber: 'LR-LEASE-2018-0143', registrationDate: '2018-01-10', surveyPlanNumber: 'SP-GBA-2018-0143', surveyDate: '2018-01-05', courtOrderNumber: '', encumbrances: 'Leasehold — 25yr term', mortgageStatus: 'Active Mortgage — IFC', disputeStatus: 'None', disputeDetails: '', previousOwner: 'Government of Liberia', transferDate: '2018-01-10', inheritanceCase: false },
    valuation: { estimatedMarketValue: '1850000', estimatedRentalValue: '18000', lastValuationDate: '2023-08-15', valuationSource: 'International Valuation', improvementValue: '1400000', landValue: '450000' },
    documents: { titleDeed: false, surveyPlan: true, nationalId: true, taxClearance: true, buildingPermit: true, occupancyCert: true, courtOrder: false, inheritanceDocs: false, photos: true, droneFootage: true },
    status: 'under_review', capturedBy: 'G. Weah', capturedDate: '2024-11-17', lastModified: '2024-12-05', assignedOfficer: 'J. Jallah', priority: 'Urgent', sentToStage2: false, notes: 'Warehouse complex near port. Leasehold — no title deed. IFC mortgage active.',
    internalFlags: ['Leasehold — no title deed', 'IFC mortgage active', 'High-value property — senior review required'],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'G. Weah', details: 'Drone survey, full boundary mapping', status: 'info', daysAgo: 51 },
      { action: 'Documents Uploaded', officer: 'G. Weah', details: 'Lease agreement, survey plan, photos uploaded', status: 'success', daysAgo: 49 },
      { action: 'Flag Added', officer: 'J. Jallah', details: 'High-value property — escalated to senior review', status: 'warning', daysAgo: 45 },
      { action: 'Status: Under Review', officer: 'J. Jallah', details: 'Senior officer review in progress', status: 'info', daysAgo: 33 },
    ]),
  },
  {
    id: 'P008', parcelId: 'LR-MON-2024-0004', registrationRef: 'REG-2024-MON-00612',
    owner: { fullName: 'Isaac P. Dolo', nationalId: 'LR-NID-0061234', taxId: 'LRA-TIN-20240612', ownershipType: 'Individual', phone: '+231-880-612-008', email: 'idolo@email.lr', postalAddress: 'Congo Town, Monrovia', nationality: 'Liberian', dateOfBirth: '1990-02-14', gender: 'Male', occupation: 'Engineer', coOwners: '' },
    location: { county: 'Montserrado', district: 'Congo Town', city: 'Monrovia', community: 'Congo Town', streetAddress: 'Congo Town Road', landmark: 'Near Congo Town Market', postalCode: '1002', lat: '6.3450', lng: '-10.8120', elevation: '30m', geoSource: 'GPS Field Survey', geoAccuracy: '±2m', geoDate: '2024-10-28', geoOfficer: 'B. Togba' },
    property: { propertyClass: 'Residential', subClass: 'Single-Family Dwelling', tenureType: 'Freehold', areaSqm: '340', buildingAreaSqm: '280', floors: '2', yearBuilt: '2005', constructionType: 'Concrete Block', roofType: 'Zinc/Metal', wallMaterial: 'Concrete Block', utilities: ['Electricity', 'Water'], accessRoad: 'Paved', boundaryType: 'Iron Fence', irrigated: false, cropType: '' },
    legal: { titleDeedNumber: 'TD-MON-2005-0612', titleDeedDate: '2005-11-20', registrationNumber: 'LR-REG-2005-0612', registrationDate: '2005-12-01', surveyPlanNumber: 'SP-MON-2005-0612', surveyDate: '2005-10-15', courtOrderNumber: '', encumbrances: 'None', mortgageStatus: 'Clear', disputeStatus: 'None', disputeDetails: '', previousOwner: 'P. Dolo (Father)', transferDate: '2005-11-20', inheritanceCase: false },
    valuation: { estimatedMarketValue: '78000', estimatedRentalValue: '600', lastValuationDate: '2023-04-10', valuationSource: 'LRA Field Assessment', improvementValue: '50000', landValue: '28000' },
    documents: { titleDeed: true, surveyPlan: true, nationalId: true, taxClearance: true, buildingPermit: true, occupancyCert: true, courtOrder: false, inheritanceDocs: false, photos: true, droneFootage: false },
    status: 'sent_to_stage2', capturedBy: 'B. Togba', capturedDate: '2024-10-28', lastModified: '2024-11-10', assignedOfficer: 'J. Jallah', priority: 'Normal', sentToStage2: true, notes: 'Verified and dispatched to Stage 2.',
    internalFlags: [],
    auditTrail: makeAudit([
      { action: 'Property Captured', officer: 'B. Togba', details: 'GPS survey completed', status: 'info', daysAgo: 71 },
      { action: 'Documents Uploaded', officer: 'B. Togba', details: 'Full document set uploaded', status: 'success', daysAgo: 69 },
      { action: 'Classification Set', officer: 'J. Jallah', details: 'Residential — Single-Family', status: 'info', daysAgo: 65 },
      { action: 'Status: Verified', officer: 'J. Jallah', details: 'All checks passed', status: 'success', daysAgo: 62 },
      { action: 'Dispatched to Stage 2', officer: 'J. Jallah', details: 'Sent to Parcel Delineation for cadastral mapping', status: 'success', daysAgo: 58 },
    ]),
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) => `$${n.toLocaleString()}`;
const docCount = (d: DocumentRecord) => Object.values(d).filter(Boolean).length;
const docTotal = 10;

// ─── Registration Wizard ──────────────────────────────────────────────────────

type WizardStep = 'owner' | 'location' | 'property' | 'legal' | 'valuation' | 'documents' | 'review';

const WIZARD_STEPS: { id: WizardStep; label: string; icon: React.ElementType }[] = [
  { id: 'owner', label: 'Owner / Entity', icon: User },
  { id: 'location', label: 'Location & GPS', icon: MapPin },
  { id: 'property', label: 'Property Details', icon: Building2 },
  { id: 'legal', label: 'Legal & Tenure', icon: Shield },
  { id: 'valuation', label: 'Valuation Estimate', icon: TrendingUp },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'review', label: 'Review & Submit', icon: CheckCircle },
];

interface RegistrationWizardProps {
  onClose: () => void;
  onSubmit: (p: PropertyRecord) => void;
}

function RegistrationWizard({ onClose, onSubmit }: RegistrationWizardProps) {
  const [step, setStep] = useState<WizardStep>('owner');
  const [owner, setOwner] = useState<OwnerRecord>({ fullName: '', nationalId: '', taxId: '', ownershipType: 'Individual', phone: '', email: '', postalAddress: '', nationality: 'Liberian', dateOfBirth: '', gender: '', occupation: '', coOwners: '' });
  const [location, setLocation] = useState<LocationRecord>({ county: 'Montserrado', district: '', city: '', community: '', streetAddress: '', landmark: '', postalCode: '', lat: '', lng: '', elevation: '', geoSource: 'GPS Field Survey', geoAccuracy: '±2m', geoDate: new Date().toISOString().split('T')[0], geoOfficer: '' });
  const [property, setProperty] = useState<PropertyDetails>({ propertyClass: 'Residential', subClass: '', tenureType: 'Freehold', areaSqm: '', buildingAreaSqm: '', floors: '', yearBuilt: '', constructionType: '', roofType: '', wallMaterial: '', utilities: [], accessRoad: '', boundaryType: '', irrigated: false, cropType: '' });
  const [legal, setLegal] = useState<LegalRecord>({ titleDeedNumber: '', titleDeedDate: '', registrationNumber: '', registrationDate: '', surveyPlanNumber: '', surveyDate: '', courtOrderNumber: '', encumbrances: '', mortgageStatus: 'Clear', disputeStatus: 'None', disputeDetails: '', previousOwner: '', transferDate: '', inheritanceCase: false });
  const [valuation, setValuation] = useState<ValuationEstimate>({ estimatedMarketValue: '', estimatedRentalValue: '', lastValuationDate: '', valuationSource: '', improvementValue: '', landValue: '' });
  const [documents, setDocuments] = useState<DocumentRecord>({ titleDeed: false, surveyPlan: false, nationalId: false, taxClearance: false, buildingPermit: false, occupancyCert: false, courtOrder: false, inheritanceDocs: false, photos: false, droneFootage: false });
  const [priority, setPriority] = useState<'Normal' | 'High' | 'Urgent'>('Normal');
  const [notes, setNotes] = useState('');

  const stepIdx = WIZARD_STEPS.findIndex(s => s.id === step);
  const isLast = stepIdx === WIZARD_STEPS.length - 1;

  const inputCls = 'w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-1 focus:ring-yellow-500/40 transition-all';
  const inputStyle = { backgroundColor: 'rgba(255,255,255,0.10)', border: '1px solid var(--dark-border)', color: 'var(--dark-text)' };
  const labelCls = 'block text-xs font-medium mb-1';
  const labelStyle = { color: 'var(--dark-muted)' };

  const handleSubmit = () => {
    const now = new Date().toISOString().split('T')[0];
    const id = `P${String(Date.now()).slice(-4)}`;
    const parcelId = `LR-${location.county.slice(0, 3).toUpperCase()}-2026-${id}`;
    const newRecord: PropertyRecord = {
      id, parcelId, registrationRef: `REG-2026-${location.county.slice(0, 3).toUpperCase()}-${id}`,
      owner, location, property, legal, valuation, documents,
      status: location.lat && location.lng ? 'geolocated' : 'captured',
      capturedBy: 'Admin', capturedDate: now, lastModified: now,
      assignedOfficer: '', priority, sentToStage2: false, notes,
      internalFlags: [],
      auditTrail: [{ timestamp: now + ' ' + new Date().toTimeString().slice(0, 5), action: 'Property Registered', officer: 'Admin', details: 'New property registered via Stage 1 Registration Wizard', status: 'success' }],
    };
    onSubmit(newRecord);
  };

  const toggleUtil = (u: string) => {
    setProperty(p => ({ ...p, utilities: p.utilities.includes(u) ? p.utilities.filter(x => x !== u) : [...p.utilities, u] }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-3xl rounded-2xl overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)', maxHeight: '92vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>STAGE 1</span>
              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Government-Grade Property Registration</span>
            </div>
            <h3 className="font-bold text-base" style={{ color: 'var(--dark-text)' }}>Register New Property</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors" style={{ color: 'var(--dark-muted)' }}><X size={16} /></button>
        </div>

        {/* Step Progress */}
        <div className="px-6 py-3 border-b flex-shrink-0 overflow-x-auto" style={{ borderColor: 'var(--dark-border)' }}>
          <div className="flex items-center gap-1 min-w-max">
            {WIZARD_STEPS.map((s, i) => {
              const done = i < stepIdx;
              const active = s.id === step;
              const Icon = s.icon;
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => setStep(s.id)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={{
                      backgroundColor: active ? 'rgba(212,160,23,0.15)' : done ? 'rgba(52,211,153,0.1)' : 'transparent',
                      color: active ? 'var(--accent)' : done ? '#34d399' : 'var(--dark-muted)',
                      border: `1px solid ${active ? 'rgba(212,160,23,0.3)' : done ? 'rgba(52,211,153,0.2)' : 'transparent'}`,
                    }}
                  >
                    {done ? <CheckCircle size={11} /> : <Icon size={11} />}
                    {s.label}
                  </button>
                  {i < WIZARD_STEPS.length - 1 && <ChevronRight size={10} style={{ color: 'var(--dark-muted)', flexShrink: 0 }} />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* STEP: Owner */}
          {step === 'owner' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Owner / Entity Information</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelCls} style={labelStyle}>Full Legal Name *</label>
                  <input required className={inputCls} style={inputStyle} value={owner.fullName} onChange={e => setOwner(o => ({ ...o, fullName: e.target.value }))} placeholder="As appears on national ID or company registration" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Ownership Type *</label>
                  <select className={inputCls} style={inputStyle} value={owner.ownershipType} onChange={e => setOwner(o => ({ ...o, ownershipType: e.target.value as OwnershipType }))}>
                    {(['Individual', 'Joint Ownership', 'Corporate Entity', 'Government Agency', 'NGO/Non-Profit', 'Trust/Estate', 'Foreign National', 'Concession Holder'] as OwnershipType[]).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Nationality</label>
                  <input className={inputCls} style={inputStyle} value={owner.nationality} onChange={e => setOwner(o => ({ ...o, nationality: e.target.value }))} placeholder="e.g. Liberian" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>National ID Number *</label>
                  <input required className={inputCls} style={inputStyle} value={owner.nationalId} onChange={e => setOwner(o => ({ ...o, nationalId: e.target.value }))} placeholder="LR-NID-XXXXXXX" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>LRA Tax ID (TIN)</label>
                  <input className={inputCls} style={inputStyle} value={owner.taxId} onChange={e => setOwner(o => ({ ...o, taxId: e.target.value }))} placeholder="LRA-TIN-XXXXXXXX" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Date of Birth</label>
                  <input type="date" className={inputCls} style={inputStyle} value={owner.dateOfBirth} onChange={e => setOwner(o => ({ ...o, dateOfBirth: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Gender</label>
                  <select className={inputCls} style={inputStyle} value={owner.gender} onChange={e => setOwner(o => ({ ...o, gender: e.target.value as OwnerRecord['gender'] }))}>
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Phone Number *</label>
                  <input required className={inputCls} style={inputStyle} value={owner.phone} onChange={e => setOwner(o => ({ ...o, phone: e.target.value }))} placeholder="+231-XXX-XXX-XXX" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Email Address</label>
                  <input type="email" className={inputCls} style={inputStyle} value={owner.email} onChange={e => setOwner(o => ({ ...o, email: e.target.value }))} placeholder="owner@email.com" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Occupation / Business</label>
                  <input className={inputCls} style={inputStyle} value={owner.occupation} onChange={e => setOwner(o => ({ ...o, occupation: e.target.value }))} placeholder="e.g. Trader, Company Director" />
                </div>
                <div className="col-span-2">
                  <label className={labelCls} style={labelStyle}>Postal / Mailing Address</label>
                  <input className={inputCls} style={inputStyle} value={owner.postalAddress} onChange={e => setOwner(o => ({ ...o, postalAddress: e.target.value }))} placeholder="P.O. Box or street address for correspondence" />
                </div>
                <div className="col-span-2">
                  <label className={labelCls} style={labelStyle}>Co-Owners / Joint Owners (if any)</label>
                  <input className={inputCls} style={inputStyle} value={owner.coOwners} onChange={e => setOwner(o => ({ ...o, coOwners: e.target.value }))} placeholder="Names and IDs of co-owners, separated by semicolons" />
                </div>
              </div>
            </div>
          )}

          {/* STEP: Location */}
          {step === 'location' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Location & Geolocation Data</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={labelStyle}>County *</label>
                  <select required className={inputCls} style={inputStyle} value={location.county} onChange={e => setLocation(l => ({ ...l, county: e.target.value as County }))}>
                    {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>District *</label>
                  <input required className={inputCls} style={inputStyle} value={location.district} onChange={e => setLocation(l => ({ ...l, district: e.target.value }))} placeholder="e.g. Greater Monrovia" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>City / Town</label>
                  <input className={inputCls} style={inputStyle} value={location.city} onChange={e => setLocation(l => ({ ...l, city: e.target.value }))} placeholder="e.g. Monrovia" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Community / Neighborhood</label>
                  <input className={inputCls} style={inputStyle} value={location.community} onChange={e => setLocation(l => ({ ...l, community: e.target.value }))} placeholder="e.g. Sinkor, Congo Town" />
                </div>
                <div className="col-span-2">
                  <label className={labelCls} style={labelStyle}>Street Address *</label>
                  <input required className={inputCls} style={inputStyle} value={location.streetAddress} onChange={e => setLocation(l => ({ ...l, streetAddress: e.target.value }))} placeholder="Street name and number" />
                </div>
                <div className="col-span-2">
                  <label className={labelCls} style={labelStyle}>Nearest Landmark</label>
                  <input className={inputCls} style={inputStyle} value={location.landmark} onChange={e => setLocation(l => ({ ...l, landmark: e.target.value }))} placeholder="e.g. Near Executive Mansion, 200m from market" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Postal Code</label>
                  <input className={inputCls} style={inputStyle} value={location.postalCode} onChange={e => setLocation(l => ({ ...l, postalCode: e.target.value }))} placeholder="e.g. 1000" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Elevation (m)</label>
                  <input className={inputCls} style={inputStyle} value={location.elevation} onChange={e => setLocation(l => ({ ...l, elevation: e.target.value }))} placeholder="e.g. 28m" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>GPS Latitude *</label>
                  <input type="number" step="any" className={inputCls} style={inputStyle} value={location.lat} onChange={e => setLocation(l => ({ ...l, lat: e.target.value }))} placeholder="e.g. 6.3005" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>GPS Longitude *</label>
                  <input type="number" step="any" className={inputCls} style={inputStyle} value={location.lng} onChange={e => setLocation(l => ({ ...l, lng: e.target.value }))} placeholder="e.g. -10.7969" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Geolocation Source *</label>
                  <select className={inputCls} style={inputStyle} value={location.geoSource} onChange={e => setLocation(l => ({ ...l, geoSource: e.target.value as GeoSource }))}>
                    {(['GPS Field Survey', 'Drone Survey', 'Satellite Imagery', 'Manual Entry', 'LIDAR', 'Cadastral Map'] as GeoSource[]).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>GPS Accuracy</label>
                  <input className={inputCls} style={inputStyle} value={location.geoAccuracy} onChange={e => setLocation(l => ({ ...l, geoAccuracy: e.target.value }))} placeholder="e.g. ±2m" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Survey Date</label>
                  <input type="date" className={inputCls} style={inputStyle} value={location.geoDate} onChange={e => setLocation(l => ({ ...l, geoDate: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Field Officer</label>
                  <select className={inputCls} style={inputStyle} value={location.geoOfficer} onChange={e => setLocation(l => ({ ...l, geoOfficer: e.target.value }))}>
                    <option value="">Select officer</option>
                    {OFFICERS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP: Property Details */}
          {step === 'property' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Property Physical Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={labelStyle}>Property Classification *</label>
                  <select required className={inputCls} style={inputStyle} value={property.propertyClass} onChange={e => setProperty(p => ({ ...p, propertyClass: e.target.value as PropertyClass }))}>
                    {PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Sub-Classification</label>
                  <input className={inputCls} style={inputStyle} value={property.subClass} onChange={e => setProperty(p => ({ ...p, subClass: e.target.value }))} placeholder="e.g. Single-Family, Retail, Warehouse" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Tenure Type *</label>
                  <select required className={inputCls} style={inputStyle} value={property.tenureType} onChange={e => setProperty(p => ({ ...p, tenureType: e.target.value as TenureType }))}>
                    {(['Freehold', 'Leasehold', 'Customary', 'Government Lease', 'Concession', 'Disputed', 'Unknown'] as TenureType[]).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Total Land Area (m²) *</label>
                  <input required type="number" min="1" className={inputCls} style={inputStyle} value={property.areaSqm} onChange={e => setProperty(p => ({ ...p, areaSqm: e.target.value }))} placeholder="e.g. 420" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Building/Improvement Area (m²)</label>
                  <input type="number" min="0" className={inputCls} style={inputStyle} value={property.buildingAreaSqm} onChange={e => setProperty(p => ({ ...p, buildingAreaSqm: e.target.value }))} placeholder="e.g. 380" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Number of Floors</label>
                  <input type="number" min="0" className={inputCls} style={inputStyle} value={property.floors} onChange={e => setProperty(p => ({ ...p, floors: e.target.value }))} placeholder="e.g. 3" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Year Built</label>
                  <input type="number" min="1900" max="2026" className={inputCls} style={inputStyle} value={property.yearBuilt} onChange={e => setProperty(p => ({ ...p, yearBuilt: e.target.value }))} placeholder="e.g. 2008" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Construction Type</label>
                  <input className={inputCls} style={inputStyle} value={property.constructionType} onChange={e => setProperty(p => ({ ...p, constructionType: e.target.value }))} placeholder="e.g. Reinforced Concrete, Mud Brick" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Roof Type</label>
                  <input className={inputCls} style={inputStyle} value={property.roofType} onChange={e => setProperty(p => ({ ...p, roofType: e.target.value }))} placeholder="e.g. Zinc/Metal, Flat/Concrete" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Wall Material</label>
                  <input className={inputCls} style={inputStyle} value={property.wallMaterial} onChange={e => setProperty(p => ({ ...p, wallMaterial: e.target.value }))} placeholder="e.g. Concrete Block, Steel" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Access Road Type</label>
                  <select className={inputCls} style={inputStyle} value={property.accessRoad} onChange={e => setProperty(p => ({ ...p, accessRoad: e.target.value }))}>
                    <option value="">Select</option>
                    {['Paved', 'Gravel', 'Dirt', 'None'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Boundary Type</label>
                  <input className={inputCls} style={inputStyle} value={property.boundaryType} onChange={e => setProperty(p => ({ ...p, boundaryType: e.target.value }))} placeholder="e.g. Concrete Wall, Iron Fence, Natural" />
                </div>
                <div className="col-span-2">
                  <label className={labelCls} style={labelStyle}>Available Utilities</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Electricity', 'Water', 'Internet', 'Generator', 'Solar'].map(u => (
                      <button key={u} type="button" onClick={() => toggleUtil(u)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                        style={{ backgroundColor: property.utilities.includes(u) ? 'rgba(212,160,23,0.15)' : 'rgba(255,255,255,0.05)', color: property.utilities.includes(u) ? 'var(--accent)' : 'var(--dark-muted)', border: `1px solid ${property.utilities.includes(u) ? 'rgba(212,160,23,0.3)' : 'var(--dark-border)'}` }}>
                        {u}
                      </button>
                    ))}
                  </div>
                </div>
                {(property.propertyClass === 'Farmland' || property.propertyClass === 'Concession Land') && (
                  <>
                    <div>
                      <label className={labelCls} style={labelStyle}>Irrigated?</label>
                      <select className={inputCls} style={inputStyle} value={property.irrigated ? 'yes' : 'no'} onChange={e => setProperty(p => ({ ...p, irrigated: e.target.value === 'yes' }))}>
                        <option value="no">No</option>
                        <option value="yes">Yes</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelCls} style={labelStyle}>Crop / Land Use Type</label>
                      <input className={inputCls} style={inputStyle} value={property.cropType} onChange={e => setProperty(p => ({ ...p, cropType: e.target.value }))} placeholder="e.g. Rubber, Rice, Cocoa" />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* STEP: Legal */}
          {step === 'legal' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Legal, Tenure & Encumbrance Records</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={labelStyle}>Title Deed Number</label>
                  <input className={inputCls} style={inputStyle} value={legal.titleDeedNumber} onChange={e => setLegal(l => ({ ...l, titleDeedNumber: e.target.value }))} placeholder="TD-XXX-YYYY-NNNN" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Title Deed Date</label>
                  <input type="date" className={inputCls} style={inputStyle} value={legal.titleDeedDate} onChange={e => setLegal(l => ({ ...l, titleDeedDate: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Land Registration Number</label>
                  <input className={inputCls} style={inputStyle} value={legal.registrationNumber} onChange={e => setLegal(l => ({ ...l, registrationNumber: e.target.value }))} placeholder="LR-REG-YYYY-NNNN" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Registration Date</label>
                  <input type="date" className={inputCls} style={inputStyle} value={legal.registrationDate} onChange={e => setLegal(l => ({ ...l, registrationDate: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Survey Plan Number</label>
                  <input className={inputCls} style={inputStyle} value={legal.surveyPlanNumber} onChange={e => setLegal(l => ({ ...l, surveyPlanNumber: e.target.value }))} placeholder="SP-XXX-YYYY-NNNN" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Survey Date</label>
                  <input type="date" className={inputCls} style={inputStyle} value={legal.surveyDate} onChange={e => setLegal(l => ({ ...l, surveyDate: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Court Order Number (if any)</label>
                  <input className={inputCls} style={inputStyle} value={legal.courtOrderNumber} onChange={e => setLegal(l => ({ ...l, courtOrderNumber: e.target.value }))} placeholder="Court order reference" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Mortgage Status</label>
                  <select className={inputCls} style={inputStyle} value={legal.mortgageStatus} onChange={e => setLegal(l => ({ ...l, mortgageStatus: e.target.value }))}>
                    {['Clear', 'Active Mortgage — LIB Bank', 'Active Mortgage — IFC', 'Active Mortgage — Other', 'Unknown'].map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <label className={labelCls} style={labelStyle}>Encumbrances / Liens</label>
                  <input className={inputCls} style={inputStyle} value={legal.encumbrances} onChange={e => setLegal(l => ({ ...l, encumbrances: e.target.value }))} placeholder="e.g. None, Easement, Lien, Mortgage" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Dispute Status</label>
                  <select className={inputCls} style={inputStyle} value={legal.disputeStatus} onChange={e => setLegal(l => ({ ...l, disputeStatus: e.target.value as LegalRecord['disputeStatus'] }))}>
                    {['None', 'Active', 'Resolved', 'Pending Court'].map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Previous Owner</label>
                  <input className={inputCls} style={inputStyle} value={legal.previousOwner} onChange={e => setLegal(l => ({ ...l, previousOwner: e.target.value }))} placeholder="Name of previous owner" />
                </div>
                {legal.disputeStatus !== 'None' && (
                  <div className="col-span-2">
                    <label className={labelCls} style={labelStyle}>Dispute Details</label>
                    <textarea rows={2} className={inputCls} style={inputStyle} value={legal.disputeDetails} onChange={e => setLegal(l => ({ ...l, disputeDetails: e.target.value }))} placeholder="Describe the nature of the dispute" />
                  </div>
                )}
                <div>
                  <label className={labelCls} style={labelStyle}>Transfer Date</label>
                  <input type="date" className={inputCls} style={inputStyle} value={legal.transferDate} onChange={e => setLegal(l => ({ ...l, transferDate: e.target.value }))} />
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <input type="checkbox" id="inheritance" checked={legal.inheritanceCase} onChange={e => setLegal(l => ({ ...l, inheritanceCase: e.target.checked }))} className="w-4 h-4 rounded" />
                  <label htmlFor="inheritance" className="text-xs font-medium" style={{ color: 'var(--dark-muted)' }}>Inheritance / Probate Case</label>
                </div>
              </div>
            </div>
          )}

          {/* STEP: Valuation */}
          {step === 'valuation' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Preliminary Valuation Estimate</h4>
              <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.2)' }}>
                <div className="flex items-start gap-2">
                  <Info size={13} style={{ color: '#60a5fa', marginTop: 1 }} />
                  <p className="text-xs" style={{ color: '#93c5fd' }}>This is a preliminary field estimate only. Official valuation will be conducted in Stage 4 using LRA-approved formulas. These values inform classification and priority.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls} style={labelStyle}>Estimated Market Value (USD)</label>
                  <input type="number" min="0" className={inputCls} style={inputStyle} value={valuation.estimatedMarketValue} onChange={e => setValuation(v => ({ ...v, estimatedMarketValue: e.target.value }))} placeholder="e.g. 85000" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Estimated Annual Rental Value (USD)</label>
                  <input type="number" min="0" className={inputCls} style={inputStyle} value={valuation.estimatedRentalValue} onChange={e => setValuation(v => ({ ...v, estimatedRentalValue: e.target.value }))} placeholder="e.g. 2400" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Land Value (USD)</label>
                  <input type="number" min="0" className={inputCls} style={inputStyle} value={valuation.landValue} onChange={e => setValuation(v => ({ ...v, landValue: e.target.value }))} placeholder="e.g. 40000" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Improvement Value (USD)</label>
                  <input type="number" min="0" className={inputCls} style={inputStyle} value={valuation.improvementValue} onChange={e => setValuation(v => ({ ...v, improvementValue: e.target.value }))} placeholder="e.g. 45000" />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Last Valuation Date</label>
                  <input type="date" className={inputCls} style={inputStyle} value={valuation.lastValuationDate} onChange={e => setValuation(v => ({ ...v, lastValuationDate: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls} style={labelStyle}>Valuation Source</label>
                  <select className={inputCls} style={inputStyle} value={valuation.valuationSource} onChange={e => setValuation(v => ({ ...v, valuationSource: e.target.value }))}>
                    <option value="">Select source</option>
                    {['LRA Field Assessment', 'Ministry of Finance', 'Ministry of Agriculture', 'International Valuation', 'Self-Declared', 'Unknown'].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* STEP: Documents */}
          {step === 'documents' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Document Checklist</h4>
              <div className="grid grid-cols-2 gap-3">
                {([
                  { key: 'titleDeed', label: 'Title Deed', required: true },
                  { key: 'surveyPlan', label: 'Survey Plan', required: true },
                  { key: 'nationalId', label: 'National ID / Passport', required: true },
                  { key: 'taxClearance', label: 'Tax Clearance Certificate', required: true },
                  { key: 'buildingPermit', label: 'Building Permit', required: false },
                  { key: 'occupancyCert', label: 'Occupancy Certificate', required: false },
                  { key: 'courtOrder', label: 'Court Order (if applicable)', required: false },
                  { key: 'inheritanceDocs', label: 'Inheritance / Probate Docs', required: false },
                  { key: 'photos', label: 'Property Photos', required: true },
                  { key: 'droneFootage', label: 'Drone / Aerial Footage', required: false },
                ] as Array<{ key: keyof DocumentRecord; label: string; required: boolean }>).map(doc => (
                  <button
                    key={doc.key}
                    type="button"
                    onClick={() => setDocuments(d => ({ ...d, [doc.key]: !d[doc.key] }))}
                    className="flex items-center gap-3 p-3 rounded-xl text-left transition-all"
                    style={{
                      backgroundColor: documents[doc.key] ? 'rgba(52,211,153,0.1)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${documents[doc.key] ? 'rgba(52,211,153,0.3)' : 'var(--dark-border)'}`,
                    }}
                  >
                    {documents[doc.key]
                      ? <CheckCircle size={16} style={{ color: '#34d399', flexShrink: 0 }} />
                      : <Square size={16} style={{ color: 'var(--dark-muted)', flexShrink: 0 }} />}
                    <div>
                      <div className="text-xs font-medium" style={{ color: documents[doc.key] ? '#34d399' : 'var(--dark-text)' }}>{doc.label}</div>
                      {doc.required && <div className="text-xs" style={{ color: '#f87171' }}>Required</div>}
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-4">
                <label className={labelCls} style={labelStyle}>Priority Level</label>
                <div className="flex gap-2 mt-1">
                  {(['Normal', 'High', 'Urgent'] as const).map(p => (
                    <button key={p} type="button" onClick={() => setPriority(p)}
                      className="flex-1 py-2 rounded-lg text-xs font-semibold transition-all"
                      style={{
                        backgroundColor: priority === p ? (p === 'Urgent' ? 'rgba(248,113,113,0.15)' : p === 'High' ? 'rgba(251,191,36,0.15)' : 'rgba(52,211,153,0.15)') : 'rgba(255,255,255,0.04)',
                        color: priority === p ? (p === 'Urgent' ? '#f87171' : p === 'High' ? '#fbbf24' : '#34d399') : 'var(--dark-muted)',
                        border: `1px solid ${priority === p ? (p === 'Urgent' ? 'rgba(248,113,113,0.3)' : p === 'High' ? 'rgba(251,191,36,0.3)' : 'rgba(52,211,153,0.3)') : 'var(--dark-border)'}`,
                      }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={labelCls} style={labelStyle}>Field Notes / Observations</label>
                <textarea rows={3} className={inputCls} style={inputStyle} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any additional observations, access issues, or special circumstances…" />
              </div>
            </div>
          )}

          {/* STEP: Review */}
          {step === 'review' && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--dark-text)' }}>Review & Confirm Registration</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Owner', value: owner.fullName || '—', color: 'var(--dark-text)' },
                  { label: 'National ID', value: owner.nationalId || '—', color: 'var(--dark-muted)' },
                  { label: 'County', value: location.county, color: 'var(--dark-text)' },
                  { label: 'Address', value: location.streetAddress || '—', color: 'var(--dark-muted)' },
                  { label: 'Classification', value: property.propertyClass, color: CLASS_COLORS[property.propertyClass] },
                  { label: 'Tenure', value: property.tenureType, color: 'var(--dark-muted)' },
                  { label: 'Area', value: property.areaSqm ? `${property.areaSqm} m²` : '—', color: 'var(--dark-text)' },
                  { label: 'GPS', value: location.lat && location.lng ? `${location.lat}, ${location.lng}` : 'Not set', color: location.lat ? '#34d399' : '#f87171' },
                  { label: 'Dispute Status', value: legal.disputeStatus, color: legal.disputeStatus !== 'None' ? '#f87171' : '#34d399' },
                  { label: 'Documents', value: `${docCount(documents)}/${docTotal} uploaded`, color: docCount(documents) >= 4 ? '#34d399' : '#fbbf24' },
                  { label: 'Priority', value: priority, color: priority === 'Urgent' ? '#f87171' : priority === 'High' ? '#fbbf24' : '#34d399' },
                  { label: 'Est. Market Value', value: valuation.estimatedMarketValue ? fmt(parseInt(valuation.estimatedMarketValue)) : 'Not set', color: 'var(--dark-text)' },
                ].map(item => (
                  <div key={item.label} className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid var(--dark-border)' }}>
                    <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{item.label}</div>
                    <div className="text-sm font-semibold" style={{ color: item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>
              {notes && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid var(--dark-border)' }}>
                  <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Field Notes</div>
                  <div className="text-xs" style={{ color: 'var(--dark-text)' }}>{notes}</div>
                </div>
              )}
              <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(212,160,23,0.08)', border: '1px solid rgba(212,160,23,0.2)' }}>
                <div className="flex items-start gap-2">
                  <Shield size={13} style={{ color: 'var(--accent)', marginTop: 1 }} />
                  <p className="text-xs" style={{ color: 'var(--accent)' }}>By submitting, you certify that all information provided is accurate and complete to the best of your knowledge. This record will be logged with your officer ID and timestamp in the LRA audit trail.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <button
            type="button"
            onClick={() => stepIdx > 0 && setStep(WIZARD_STEPS[stepIdx - 1].id)}
            disabled={stepIdx === 0}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors hover:bg-white/5 disabled:opacity-30"
            style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}
          >
            ← Back
          </button>
          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Step {stepIdx + 1} of {WIZARD_STEPS.length}</span>
          {isLast ? (
            <button
              type="button"
              onClick={handleSubmit}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-colors"
              style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}
            >
              <CheckCircle size={14} /> Submit Registration
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep(WIZARD_STEPS[stepIdx + 1].id)}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-colors"
              style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}
            >
              Next → {WIZARD_STEPS[stepIdx + 1].label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Bulk Import Modal ────────────────────────────────────────────────────────

function BulkImportModal({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<'upload' | 'preview' | 'done'>('upload');
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');

  const previewRows = [
    { parcelId: 'LR-MON-2026-0050', owner: 'K. Brownell', county: 'Montserrado', class: 'Residential', area: '310 m²', lat: '6.3100', lng: '-10.8050', status: 'OK' },
    { parcelId: 'LR-NIM-2026-0030', owner: 'S. Gaye', county: 'Nimba', class: 'Farmland', area: '8400 m²', lat: '7.3500', lng: '-8.7100', status: 'OK' },
    { parcelId: 'LR-BON-2026-0020', owner: 'M. Kpehe', county: 'Bong', class: 'Commercial', area: '520 m²', lat: '6.9900', lng: '-9.4800', status: 'WARN: No TIN' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-2xl rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          <div>
            <h3 className="font-bold text-base" style={{ color: 'var(--dark-text)' }}>Bulk Property Import</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Stage 1 — Batch registration via CSV/Excel</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5" style={{ color: 'var(--dark-muted)' }}><X size={16} /></button>
        </div>
        <div className="p-5">
          {step === 'upload' && (
            <div>
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) { setFileName(f.name); setStep('preview'); } }}
                className="border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer"
                style={{ borderColor: dragging ? 'var(--accent)' : 'var(--dark-border)', backgroundColor: dragging ? 'rgba(212,160,23,0.05)' : 'var(--dark-elevated)' }}
                onClick={() => { setFileName('lra_properties_batch_2026.csv'); setStep('preview'); }}
              >
                <Upload size={32} className="mx-auto mb-3" style={{ color: 'var(--accent)' }} />
                <p className="font-semibold text-sm mb-1" style={{ color: 'var(--dark-text)' }}>Drop CSV / Excel file here</p>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>or click to browse — max 10,000 rows per batch</p>
              </div>
              <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--dark-elevated)', border: '1px solid var(--dark-border)' }}>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>Required columns:</p>
                <div className="flex flex-wrap gap-2">
                  {['owner_name', 'national_id', 'county', 'district', 'street_address', 'property_class', 'tenure_type', 'area_sqm', 'latitude', 'longitude', 'geo_source'].map(col => (
                    <span key={col} className="px-2 py-0.5 rounded text-xs font-mono" style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)' }}>{col}</span>
                  ))}
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <button className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors hover:bg-white/5" style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
                  <Download size={12} /> Download Template
                </button>
                <button className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg transition-colors hover:bg-white/5" style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>
                  <FileText size={12} /> Import Guide (PDF)
                </button>
              </div>
            </div>
          )}
          {step === 'preview' && (
            <div>
              <div className="flex items-center gap-2 mb-4 p-2 rounded-lg" style={{ backgroundColor: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)' }}>
                <CheckCircle size={14} style={{ color: '#34d399' }} />
                <span className="text-xs font-medium" style={{ color: '#34d399' }}>{fileName} — 3 records parsed, 1 warning, 0 errors</span>
              </div>
              <div className="overflow-x-auto rounded-lg" style={{ border: '1px solid var(--dark-border)' }}>
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--dark-elevated)' }}>
                      {['Parcel ID', 'Owner', 'County', 'Class', 'Area', 'Lat', 'Lng', 'Validation'].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-semibold" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: 'var(--dark-border)' }}>
                        <td className="px-3 py-2 font-mono" style={{ color: 'var(--accent)' }}>{r.parcelId}</td>
                        <td className="px-3 py-2" style={{ color: 'var(--dark-text)' }}>{r.owner}</td>
                        <td className="px-3 py-2" style={{ color: 'var(--dark-muted)' }}>{r.county}</td>
                        <td className="px-3 py-2" style={{ color: 'var(--dark-muted)' }}>{r.class}</td>
                        <td className="px-3 py-2" style={{ color: 'var(--dark-muted)' }}>{r.area}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: 'var(--dark-muted)' }}>{r.lat}</td>
                        <td className="px-3 py-2 font-mono" style={{ color: 'var(--dark-muted)' }}>{r.lng}</td>
                        <td className="px-3 py-2">
                          <span className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: r.status === 'OK' ? 'rgba(52,211,153,0.1)' : 'rgba(251,191,36,0.1)', color: r.status === 'OK' ? '#34d399' : '#fbbf24' }}>{r.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={() => setStep('upload')} className="px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors" style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>Back</button>
                <button onClick={() => setStep('done')} className="px-5 py-2 rounded-lg text-sm font-bold transition-colors" style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}>Import 3 Properties</button>
              </div>
            </div>
          )}
          {step === 'done' && (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(52,211,153,0.15)' }}>
                <CheckCircle size={28} style={{ color: '#34d399' }} />
              </div>
              <h4 className="font-bold text-base mb-1" style={{ color: 'var(--dark-text)' }}>Import Successful</h4>
              <p className="text-sm mb-4" style={{ color: 'var(--dark-muted)' }}>3 properties registered. 1 flagged for TIN verification. All queued for geolocation review.</p>
              <button onClick={onClose} className="px-5 py-2 rounded-lg text-sm font-bold" style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}>Done</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

function DetailDrawer({ record, onClose, onStatusChange }: { record: PropertyRecord; onClose: () => void; onStatusChange: (id: string, status: RegistrationStatus) => void }) {
  const [tab, setTab] = useState<'overview' | 'legal' | 'documents' | 'audit'>('overview');
  const sc = STATUS_CONFIG[record.status];

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}>
      <div className="w-full max-w-2xl rounded-2xl overflow-hidden flex flex-col" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)', maxHeight: '90vh' }}>
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono px-2 py-0.5 rounded" style={{ backgroundColor: 'rgba(212,160,23,0.1)', color: 'var(--accent)' }}>{record.parcelId}</span>
              <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
              {record.priority !== 'Normal' && (
                <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ backgroundColor: record.priority === 'Urgent' ? 'rgba(248,113,113,0.12)' : 'rgba(251,191,36,0.12)', color: record.priority === 'Urgent' ? '#f87171' : '#fbbf24' }}>{record.priority}</span>
              )}
            </div>
            <h3 className="font-bold text-base" style={{ color: 'var(--dark-text)' }}>{record.owner.fullName}</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{record.location.streetAddress}, {record.location.county} · {record.property.propertyClass}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5" style={{ color: 'var(--dark-muted)' }}><X size={16} /></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-3 border-b flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          {(['overview', 'legal', 'documents', 'audit'] as const).map(t => (
            <button key={t} onClick={() => setTab(t)}
              className="px-3 py-2 text-xs font-medium capitalize transition-colors border-b-2 -mb-px"
              style={{ borderColor: tab === t ? 'var(--accent)' : 'transparent', color: tab === t ? 'var(--accent)' : 'var(--dark-muted)' }}>
              {t === 'audit' ? 'Audit Trail' : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === 'overview' && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>OWNER DETAILS</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'National ID', value: record.owner.nationalId },
                    { label: 'Tax ID (TIN)', value: record.owner.taxId || 'Not on record' },
                    { label: 'Phone', value: record.owner.phone },
                    { label: 'Email', value: record.owner.email || '—' },
                    { label: 'Ownership Type', value: record.owner.ownershipType },
                    { label: 'Occupation', value: record.owner.occupation || '—' },
                    { label: 'Nationality', value: record.owner.nationality },
                    { label: 'Co-Owners', value: record.owner.coOwners || 'None' },
                  ].map(r => (
                    <div key={r.label} className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                      <div className="text-xs mb-0.5" style={{ color: 'var(--dark-muted)' }}>{r.label}</div>
                      <div className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{r.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>LOCATION</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'County', value: record.location.county },
                    { label: 'District', value: record.location.district },
                    { label: 'Community', value: record.location.community || '—' },
                    { label: 'GPS', value: `${record.location.lat}, ${record.location.lng}` },
                    { label: 'Geo Source', value: record.location.geoSource },
                    { label: 'Accuracy', value: record.location.geoAccuracy },
                    { label: 'Survey Date', value: record.location.geoDate },
                    { label: 'Field Officer', value: record.location.geoOfficer || '—' },
                  ].map(r => (
                    <div key={r.label} className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                      <div className="text-xs mb-0.5" style={{ color: 'var(--dark-muted)' }}>{r.label}</div>
                      <div className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{r.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>PROPERTY</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { label: 'Classification', value: record.property.propertyClass },
                    { label: 'Sub-Class', value: record.property.subClass || '—' },
                    { label: 'Land Area', value: `${record.property.areaSqm} m²` },
                    { label: 'Building Area', value: record.property.buildingAreaSqm ? `${record.property.buildingAreaSqm} m²` : '—' },
                    { label: 'Floors', value: record.property.floors || '—' },
                    { label: 'Year Built', value: record.property.yearBuilt || '—' },
                    { label: 'Construction', value: record.property.constructionType || '—' },
                    { label: 'Utilities', value: record.property.utilities.join(', ') || 'None' },
                  ].map(r => (
                    <div key={r.label} className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                      <div className="text-xs mb-0.5" style={{ color: 'var(--dark-muted)' }}>{r.label}</div>
                      <div className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{r.value}</div>
                    </div>
                  ))}
                </div>
              </div>
              {record.valuation.estimatedMarketValue && (
                <div>
                  <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>PRELIMINARY VALUATION</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { label: 'Est. Market Value', value: fmt(parseInt(record.valuation.estimatedMarketValue)) },
                      { label: 'Est. Rental Value', value: record.valuation.estimatedRentalValue ? fmt(parseInt(record.valuation.estimatedRentalValue)) + '/yr' : '—' },
                      { label: 'Land Value', value: record.valuation.landValue ? fmt(parseInt(record.valuation.landValue)) : '—' },
                      { label: 'Improvement Value', value: record.valuation.improvementValue ? fmt(parseInt(record.valuation.improvementValue)) : '—' },
                    ].map(r => (
                      <div key={r.label} className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                        <div className="text-xs mb-0.5" style={{ color: 'var(--dark-muted)' }}>{r.label}</div>
                        <div className="text-xs font-medium" style={{ color: '#34d399' }}>{r.value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {record.internalFlags.length > 0 && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
                  <p className="text-xs font-semibold mb-2" style={{ color: '#f87171' }}>INTERNAL FLAGS</p>
                  {record.internalFlags.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs mb-1" style={{ color: '#fca5a5' }}>
                      <AlertTriangle size={11} /> {f}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'legal' && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Title Deed No.', value: record.legal.titleDeedNumber || 'Not available' },
                  { label: 'Title Deed Date', value: record.legal.titleDeedDate || '—' },
                  { label: 'Registration No.', value: record.legal.registrationNumber || '—' },
                  { label: 'Registration Date', value: record.legal.registrationDate || '—' },
                  { label: 'Survey Plan No.', value: record.legal.surveyPlanNumber || '—' },
                  { label: 'Survey Date', value: record.legal.surveyDate || '—' },
                  { label: 'Mortgage Status', value: record.legal.mortgageStatus },
                  { label: 'Encumbrances', value: record.legal.encumbrances || 'None' },
                  { label: 'Dispute Status', value: record.legal.disputeStatus },
                  { label: 'Previous Owner', value: record.legal.previousOwner || '—' },
                  { label: 'Transfer Date', value: record.legal.transferDate || '—' },
                  { label: 'Inheritance Case', value: record.legal.inheritanceCase ? 'Yes' : 'No' },
                ].map(r => (
                  <div key={r.label} className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)' }}>
                    <div className="text-xs mb-0.5" style={{ color: 'var(--dark-muted)' }}>{r.label}</div>
                    <div className="text-xs font-medium" style={{ color: r.label === 'Dispute Status' && record.legal.disputeStatus !== 'None' ? '#f87171' : 'var(--dark-text)' }}>{r.value}</div>
                  </div>
                ))}
              </div>
              {record.legal.disputeDetails && (
                <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: '#f87171' }}>Dispute Details</p>
                  <p className="text-xs" style={{ color: '#fca5a5' }}>{record.legal.disputeDetails}</p>
                </div>
              )}
            </div>
          )}

          {tab === 'documents' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>DOCUMENT STATUS</p>
                <span className="text-xs font-bold" style={{ color: docCount(record.documents) >= 4 ? '#34d399' : '#fbbf24' }}>{docCount(record.documents)}/{docTotal} uploaded</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { key: 'titleDeed', label: 'Title Deed', required: true },
                  { key: 'surveyPlan', label: 'Survey Plan', required: true },
                  { key: 'nationalId', label: 'National ID', required: true },
                  { key: 'taxClearance', label: 'Tax Clearance', required: true },
                  { key: 'buildingPermit', label: 'Building Permit', required: false },
                  { key: 'occupancyCert', label: 'Occupancy Cert.', required: false },
                  { key: 'courtOrder', label: 'Court Order', required: false },
                  { key: 'inheritanceDocs', label: 'Inheritance Docs', required: false },
                  { key: 'photos', label: 'Property Photos', required: true },
                  { key: 'droneFootage', label: 'Drone Footage', required: false },
                ] as Array<{ key: keyof DocumentRecord; label: string; required: boolean }>).map(doc => (
                  <div key={doc.key} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: `1px solid ${record.documents[doc.key] ? 'rgba(52,211,153,0.2)' : doc.required ? 'rgba(248,113,113,0.2)' : 'var(--dark-border)'}` }}>
                    {record.documents[doc.key]
                      ? <CheckCircle size={13} style={{ color: '#34d399', flexShrink: 0 }} />
                      : <AlertCircle size={13} style={{ color: doc.required ? '#f87171' : '#6b7280', flexShrink: 0 }} />}
                    <div>
                      <div className="text-xs font-medium" style={{ color: record.documents[doc.key] ? '#34d399' : 'var(--dark-muted)' }}>{doc.label}</div>
                      {doc.required && !record.documents[doc.key] && <div className="text-xs" style={{ color: '#f87171' }}>Required</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'audit' && (
            <div className="space-y-2">
              <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-muted)' }}>AUDIT TRAIL — {record.auditTrail.length} entries</p>
              {record.auditTrail.map((entry, i) => {
                const colors = { info: '#60a5fa', success: '#34d399', warning: '#fbbf24', error: '#f87171' };
                return (
                  <div key={i} className="flex gap-3 p-3 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid var(--dark-border)' }}>
                    <div className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: colors[entry.status] }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="text-xs font-semibold" style={{ color: colors[entry.status] }}>{entry.action}</span>
                        <span className="text-xs flex-shrink-0" style={{ color: 'var(--dark-muted)' }}>{entry.timestamp}</span>
                      </div>
                      <div className="text-xs mb-0.5" style={{ color: 'var(--dark-text)' }}>{entry.details}</div>
                      <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>Officer: {entry.officer}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-2 px-5 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--dark-border)' }}>
          <select
            value={record.status}
            onChange={e => onStatusChange(record.id, e.target.value as RegistrationStatus)}
            className="flex-1 px-3 py-2 rounded-lg text-xs border outline-none"
            style={{ backgroundColor: 'rgba(255,255,255,0.10)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }}
          >
            {(Object.keys(STATUS_CONFIG) as RegistrationStatus[]).map(s => (
              <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>
            ))}
          </select>
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-xs font-medium hover:bg-white/5 transition-colors" style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Map View ─────────────────────────────────────────────────────────────────

function MapView({ properties }: { properties: PropertyRecord[] }) {
  const [selected, setSelected] = useState<PropertyRecord | null>(null);
  const mapW = 600; const mapH = 400;
  const latMin = 4.2, latMax = 8.7, lngMin = -11.7, lngMax = -7.3;
  const toX = (lng: number) => ((lng - lngMin) / (lngMax - lngMin)) * mapW;
  const toY = (lat: number) => ((latMax - lat) / (latMax - latMin)) * mapH;

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--dark-border)', backgroundColor: 'var(--dark-elevated)' }}>
      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--dark-border)' }}>
        <div className="flex items-center gap-2">
          <MapPin size={15} style={{ color: 'var(--accent)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>Geolocation Map — Liberia (15 Counties)</span>
        </div>
        <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{properties.length} properties plotted</span>
      </div>
      <div className="relative" style={{ height: `${mapH}px`, backgroundColor: '#0d1b2a' }}>
        <svg className="absolute inset-0 w-full h-full opacity-10" viewBox={`0 0 ${mapW} ${mapH}`} preserveAspectRatio="none">
          {[...Array(8)].map((_, i) => <line key={`h${i}`} x1="0" y1={i * mapH / 7} x2={mapW} y2={i * mapH / 7} stroke="#60a5fa" strokeWidth="0.5" />)}
          {[...Array(10)].map((_, i) => <line key={`v${i}`} x1={i * mapW / 9} y1="0" x2={i * mapW / 9} y2={mapH} stroke="#60a5fa" strokeWidth="0.5" />)}
        </svg>
        <div className="absolute top-2 left-3 text-xs font-semibold opacity-30" style={{ color: '#60a5fa' }}>LIBERIA</div>
        {properties.map(p => {
          const lat = parseFloat(p.location.lat);
          const lng = parseFloat(p.location.lng);
          if (isNaN(lat) || isNaN(lng)) return null;
          const x = toX(lng); const y = toY(lat);
          const color = CLASS_COLORS[p.property.propertyClass];
          const isSelected = selected?.id === p.id;
          return (
            <button key={p.id} onClick={() => setSelected(isSelected ? null : p)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all"
              style={{ left: `${(x / mapW) * 100}%`, top: `${(y / mapH) * 100}%`, zIndex: isSelected ? 20 : 10 }}>
              <div className="rounded-full border-2 transition-all" style={{ width: isSelected ? 18 : 12, height: isSelected ? 18 : 12, backgroundColor: color, borderColor: isSelected ? '#fff' : 'rgba(255,255,255,0.3)', boxShadow: isSelected ? `0 0 12px ${color}` : 'none' }} />
            </button>
          );
        })}
        {selected && (
          <div className="absolute bottom-3 left-3 right-3 rounded-lg p-3 z-30" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-bold mb-0.5" style={{ color: 'var(--dark-text)' }}>{selected.owner.fullName}</p>
                <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>{selected.location.streetAddress} · {selected.location.county}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: `${CLASS_COLORS[selected.property.propertyClass]}20`, color: CLASS_COLORS[selected.property.propertyClass] }}>{selected.property.propertyClass}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: STATUS_CONFIG[selected.status].bg, color: STATUS_CONFIG[selected.status].color }}>{STATUS_CONFIG[selected.status].label}</span>
                  <span className="text-xs font-mono" style={{ color: 'var(--dark-muted)' }}>{selected.location.lat}, {selected.location.lng}</span>
                </div>
              </div>
              <button onClick={() => setSelected(null)} style={{ color: 'var(--dark-muted)' }}><X size={14} /></button>
            </div>
          </div>
        )}
      </div>
      <div className="px-4 py-2 border-t flex flex-wrap gap-3" style={{ borderColor: 'var(--dark-border)' }}>
        {Object.entries(CLASS_COLORS).slice(0, 7).map(([cls, color]) => (
          <div key={cls} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />
            <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{cls}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Analytics View ───────────────────────────────────────────────────────────

function AnalyticsView({ properties }: { properties: PropertyRecord[] }) {
  const countyData = COUNTIES.map(c => ({ county: c.slice(0, 6), count: properties.filter(p => p.location.county === c).length })).filter(d => d.count > 0);
  const classData = PROPERTY_CLASSES.map(c => ({ name: c, value: properties.filter(p => p.property.propertyClass === c).length, color: CLASS_COLORS[c] })).filter(d => d.value > 0);
  const statusData = (Object.keys(STATUS_CONFIG) as RegistrationStatus[]).map(s => ({ name: STATUS_CONFIG[s].label, value: properties.filter(p => p.status === s).length, color: STATUS_CONFIG[s].color })).filter(d => d.value > 0);
  const geoData = (['GPS Field Survey', 'Drone Survey', 'Satellite Imagery', 'Manual Entry', 'LIDAR', 'Cadastral Map'] as GeoSource[]).map(s => ({ source: s.split(' ')[0], count: properties.filter(p => p.location.geoSource === s).length })).filter(d => d.count > 0);
  const docCompleteness = properties.map(p => ({ id: p.parcelId.slice(-4), docs: docCount(p.documents), total: docTotal }));
  const flaggedCount = properties.filter(p => p.internalFlags.length > 0).length;
  const disputeCount = properties.filter(p => p.legal.disputeStatus !== 'None').length;
  const totalEstValue = properties.reduce((s, p) => s + (parseInt(p.valuation.estimatedMarketValue) || 0), 0);

  return (
    <div className="space-y-5">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Total Registered', value: String(properties.length), color: 'var(--dark-text)' },
          { label: 'Flagged / Issues', value: String(flaggedCount), color: '#f87171' },
          { label: 'Active Disputes', value: String(disputeCount), color: '#fbbf24' },
          { label: 'Est. Portfolio Value', value: `$${(totalEstValue / 1000000).toFixed(1)}M`, color: '#34d399' },
        ].map(k => (
          <div key={k.label} className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)' }}>
            <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{k.label}</div>
            <div className="text-2xl font-bold" style={{ color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* County Distribution */}
        <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)' }}>
          <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-muted)' }}>PROPERTIES BY COUNTY</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={countyData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <XAxis dataKey="county" tick={{ fill: 'var(--dark-muted)', fontSize: 10 }} />
              <YAxis tick={{ fill: 'var(--dark-muted)', fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)', borderRadius: 8, fontSize: 11 }} />
              <Bar dataKey="count" fill="var(--accent)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Classification Pie */}
        <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)' }}>
          <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-muted)' }}>CLASSIFICATION BREAKDOWN</p>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie data={classData} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={2}>
                {classData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Pie>
              <Tooltip contentStyle={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)', borderRadius: 8, fontSize: 11 }} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10, color: 'var(--dark-muted)' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Status Pipeline */}
        <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)' }}>
          <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-muted)' }}>REGISTRATION PIPELINE</p>
          <div className="space-y-2">
            {statusData.map(s => (
              <div key={s.name} className="flex items-center gap-3">
                <div className="text-xs w-28 flex-shrink-0" style={{ color: 'var(--dark-muted)' }}>{s.name}</div>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${(s.value / properties.length) * 100}%`, backgroundColor: s.color }} />
                </div>
                <div className="text-xs font-bold w-6 text-right" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Geo Source */}
        <div className="p-4 rounded-xl border" style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)' }}>
          <p className="text-xs font-semibold mb-3" style={{ color: 'var(--dark-muted)' }}>GEOLOCATION SOURCE</p>
          <div className="space-y-2">
            {geoData.map(g => (
              <div key={g.source} className="flex items-center gap-3">
                <div className="text-xs w-20 flex-shrink-0" style={{ color: 'var(--dark-muted)' }}>{g.source}</div>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full" style={{ width: `${(g.count / properties.length) * 100}%`, backgroundColor: '#60a5fa' }} />
                </div>
                <div className="text-xs font-bold w-6 text-right" style={{ color: '#60a5fa' }}>{g.count}</div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--dark-muted)' }}>DOCUMENT COMPLETENESS</p>
            {docCompleteness.map(d => (
              <div key={d.id} className="flex items-center gap-2 mb-1">
                <span className="text-xs font-mono w-16 flex-shrink-0" style={{ color: 'var(--accent)' }}>…{d.id}</span>
                <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full" style={{ width: `${(d.docs / d.total) * 100}%`, backgroundColor: d.docs >= 4 ? '#34d399' : '#fbbf24' }} />
                </div>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{d.docs}/{d.total}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Send to Stage 2 Modal ────────────────────────────────────────────────────

function Stage2Modal({ properties, onClose, onConfirm }: { properties: PropertyRecord[]; onClose: () => void; onConfirm: (ids: string[]) => void }) {
  const eligible = properties.filter(p => (p.status === 'verified' || p.status === 'classified') && !p.sentToStage2);
  const [selected, setSelected] = useState<Set<string>>(new Set(eligible.map(p => p.id)));
  const toggle = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}>
      <div className="w-full max-w-xl rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          <div>
            <h3 className="font-bold text-base" style={{ color: 'var(--dark-text)' }}>Dispatch to Stage 2 — Parcel Delineation</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Send verified/classified properties for cadastral boundary mapping</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5" style={{ color: 'var(--dark-muted)' }}><X size={16} /></button>
        </div>
        <div className="p-5">
          {eligible.length === 0 ? (
            <div className="text-center py-6">
              <AlertTriangle size={28} className="mx-auto mb-2" style={{ color: '#f59e0b' }} />
              <p className="text-sm" style={{ color: 'var(--dark-muted)' }}>No eligible properties. Properties must be Verified or Classified before dispatch.</p>
            </div>
          ) : (
            <>
              <p className="text-xs mb-3" style={{ color: 'var(--dark-muted)' }}>{eligible.length} eligible — select which to dispatch:</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {eligible.map(p => (
                  <button key={p.id} onClick={() => toggle(p.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors hover:bg-white/5"
                    style={{ border: `1px solid ${selected.has(p.id) ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`, backgroundColor: selected.has(p.id) ? 'rgba(212,160,23,0.05)' : 'transparent' }}>
                    {selected.has(p.id) ? <CheckSquare size={15} style={{ color: 'var(--accent)' }} /> : <Square size={15} style={{ color: 'var(--dark-muted)' }} />}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: 'var(--dark-text)' }}>{p.owner.fullName}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--dark-muted)' }}>{p.parcelId} · {p.location.county} · {p.property.propertyClass}</p>
                    </div>
                    <span className="text-xs px-1.5 py-0.5 rounded flex-shrink-0" style={{ backgroundColor: STATUS_CONFIG[p.status].bg, color: STATUS_CONFIG[p.status].color }}>{STATUS_CONFIG[p.status].label}</span>
                  </button>
                ))}
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/5 transition-colors" style={{ color: 'var(--dark-muted)', border: '1px solid var(--dark-border)' }}>Cancel</button>
                <button onClick={() => onConfirm(Array.from(selected))} disabled={selected.size === 0}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold transition-colors disabled:opacity-40"
                  style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}>
                  <Send size={14} /> Dispatch {selected.size} to Stage 2
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Client ──────────────────────────────────────────────────────────────

type ViewMode = 'list' | 'map' | 'analytics';

export default function PropertyRegistrationAdminClient() {
  const [properties, setProperties] = useState<PropertyRecord[]>(initialProperties);
  const [view, setView] = useState<ViewMode>('list');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<RegistrationStatus | 'all'>('all');
  const [filterClass, setFilterClass] = useState<PropertyClass | 'all'>('all');
  const [filterCounty, setFilterCounty] = useState<County | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<'all' | 'Normal' | 'High' | 'Urgent'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showWizard, setShowWizard] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showStage2Modal, setShowStage2Modal] = useState(false);
  const [detailRecord, setDetailRecord] = useState<PropertyRecord | null>(null);
  const [stage2Success, setStage2Success] = useState<number | null>(null);
  const [sortField, setSortField] = useState<'capturedDate' | 'owner' | 'county'>('capturedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const filtered = useMemo(() => {
    let r = properties;
    if (search) r = r.filter(p => p.owner.fullName.toLowerCase().includes(search.toLowerCase()) || p.parcelId.toLowerCase().includes(search.toLowerCase()) || p.location.streetAddress.toLowerCase().includes(search.toLowerCase()) || p.owner.nationalId.toLowerCase().includes(search.toLowerCase()));
    if (filterStatus !== 'all') r = r.filter(p => p.status === filterStatus);
    if (filterClass !== 'all') r = r.filter(p => p.property.propertyClass === filterClass);
    if (filterCounty !== 'all') r = r.filter(p => p.location.county === filterCounty);
    if (filterPriority !== 'all') r = r.filter(p => p.priority === filterPriority);
    return [...r].sort((a, b) => {
      const av = sortField === 'owner' ? a.owner.fullName : sortField === 'county' ? a.location.county : a.capturedDate;
      const bv = sortField === 'owner' ? b.owner.fullName : sortField === 'county' ? b.location.county : b.capturedDate;
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [properties, search, filterStatus, filterClass, filterCounty, filterPriority, sortField, sortDir]);

  const kpis = useMemo(() => ({
    total: properties.length,
    draft: properties.filter(p => p.status === 'draft' || p.status === 'captured').length,
    geolocated: properties.filter(p => p.status === 'geolocated').length,
    docsPending: properties.filter(p => p.status === 'documents_pending').length,
    underReview: properties.filter(p => p.status === 'under_review').length,
    verified: properties.filter(p => p.status === 'verified').length,
    flagged: properties.filter(p => p.status === 'flagged').length,
    sentToStage2: properties.filter(p => p.sentToStage2).length,
  }), [properties]);

  const toggleSelect = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map(p => p.id)));

  const handleStatusChange = (id: string, status: RegistrationStatus) => {
    setProperties(prev => prev.map(p => p.id === id ? { ...p, status, lastModified: new Date().toISOString().split('T')[0], auditTrail: [...p.auditTrail, { timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16), action: `Status Changed to ${STATUS_CONFIG[status].label}`, officer: 'Admin', details: `Status updated via detail panel`, status: 'info' as const }] } : p));
    if (detailRecord?.id === id) setDetailRecord(prev => prev ? { ...prev, status } : null);
  };

  const handleStage2Confirm = (ids: string[]) => {
    setProperties(prev => prev.map(p => ids.includes(p.id) ? { ...p, status: 'sent_to_stage2', sentToStage2: true, lastModified: new Date().toISOString().split('T')[0], auditTrail: [...p.auditTrail, { timestamp: new Date().toISOString().replace('T', ' ').slice(0, 16), action: 'Dispatched to Stage 2', officer: 'Admin', details: 'Sent to Parcel Delineation for cadastral boundary mapping', status: 'success' as const }] } : p));
    setShowStage2Modal(false);
    setStage2Success(ids.length);
    setTimeout(() => setStage2Success(null), 4000);
  };

  const VIEW_TABS = [
    { id: 'list' as ViewMode, label: 'Registry', icon: List },
    { id: 'map' as ViewMode, label: 'Map View', icon: Map },
    { id: 'analytics' as ViewMode, label: 'Analytics', icon: BarChart2 },
  ];

  return (
    <AppLayout>
      <div className="p-6 space-y-5" style={{ color: 'var(--text-primary)' }}>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Link href="/property-tax-cycle" className="flex items-center gap-1 text-xs hover:opacity-80 transition-opacity" style={{ color: 'var(--dark-muted)' }}>
                <ArrowLeft size={12} /> Tax Cycle
              </Link>
              <span style={{ color: 'var(--dark-muted)' }}>/</span>
              <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>STAGE 1</span>
              <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Property Discovery & Registration</span>
            </div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--dark-text)' }}>Property Registration — Government Registry</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--dark-muted)' }}>Capture, geolocate, classify, and legally register all taxable properties across Liberia's 15 counties</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setShowBulkModal(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: 'rgba(96,165,250,0.12)', color: '#60a5fa', border: '1px solid rgba(96,165,250,0.25)' }}>
              <Upload size={13} /> Bulk Import
            </button>
            <button onClick={() => setShowStage2Modal(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.25)' }}>
              <Send size={13} /> Dispatch to Stage 2
            </button>
            <button onClick={() => setShowWizard(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors" style={{ backgroundColor: 'var(--accent)', color: '#070e1f' }}>
              <Plus size={14} /> Register Property
            </button>
          </div>
        </div>

        {/* Success Banner */}
        {stage2Success !== null && (
          <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)' }}>
            <CheckCircle size={16} style={{ color: '#34d399' }} />
            <span className="text-sm font-medium" style={{ color: '#34d399' }}>{stage2Success} properties dispatched to Stage 2 — Parcel Delineation</span>
          </div>
        )}

        {/* KPI Strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          {[
            { label: 'Total', value: kpis.total, color: 'var(--dark-text)' },
            { label: 'Captured', value: kpis.draft, color: '#94a3b8' },
            { label: 'Geolocated', value: kpis.geolocated, color: '#60a5fa' },
            { label: 'Docs Pending', value: kpis.docsPending, color: '#fb923c' },
            { label: 'Under Review', value: kpis.underReview, color: '#a78bfa' },
            { label: 'Verified', value: kpis.verified, color: '#34d399' },
            { label: 'Flagged', value: kpis.flagged, color: '#f87171' },
            { label: '→ Stage 2', value: kpis.sentToStage2, color: '#6ee7b7' },
          ].map(k => (
            <div key={k.label} className="p-3 rounded-xl border" style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)' }}>
              <div className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{k.label}</div>
              <div className="text-xl font-bold" style={{ color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* View Tabs */}
        <div className="flex gap-1 border-b" style={{ borderColor: 'var(--dark-border)' }}>
          {VIEW_TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setView(t.id)}
                className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px"
                style={{ borderColor: view === t.id ? 'var(--accent)' : 'transparent', color: view === t.id ? 'var(--accent)' : 'var(--dark-muted)' }}>
                <Icon size={13} /> {t.label}
              </button>
            );
          })}
        </div>

        {/* LIST VIEW */}
        {view === 'list' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-48">
                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search owner, parcel ID, national ID, address…"
                  className="w-full pl-8 pr-3 py-2 rounded-lg text-sm border outline-none"
                  style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)', color: 'var(--dark-text)' }} />
              </div>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value as RegistrationStatus | 'all')}
                className="px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)', color: 'var(--dark-muted)' }}>
                <option value="all">All Statuses</option>
                {(Object.keys(STATUS_CONFIG) as RegistrationStatus[]).map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
              </select>
              <select value={filterClass} onChange={e => setFilterClass(e.target.value as PropertyClass | 'all')}
                className="px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)', color: 'var(--dark-muted)' }}>
                <option value="all">All Classes</option>
                {PROPERTY_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filterCounty} onChange={e => setFilterCounty(e.target.value as County | 'all')}
                className="px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)', color: 'var(--dark-muted)' }}>
                <option value="all">All Counties</option>
                {COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              <select value={filterPriority} onChange={e => setFilterPriority(e.target.value as typeof filterPriority)}
                className="px-3 py-2 rounded-lg text-sm border outline-none"
                style={{ backgroundColor: 'var(--dark-surface)', borderColor: 'var(--dark-border)', color: 'var(--dark-muted)' }}>
                <option value="all">All Priorities</option>
                <option value="Urgent">Urgent</option>
                <option value="High">High</option>
                <option value="Normal">Normal</option>
              </select>
            </div>

            {/* Table */}
            <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--dark-border)' }}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--dark-border)' }}>
                      <th className="px-3 py-3 text-left w-8">
                        <button onClick={toggleAll}>
                          {selected.size === filtered.length && filtered.length > 0
                            ? <CheckSquare size={14} style={{ color: '#60a5fa' }} />
                            : <Square size={14} style={{ color: 'var(--dark-muted)' }} />}
                        </button>
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold cursor-pointer" style={{ color: 'var(--dark-muted)' }} onClick={() => { setSortField('owner'); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
                        Owner / Parcel ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Classification</th>
                      <th className="px-3 py-3 text-left text-xs font-semibold cursor-pointer" style={{ color: 'var(--dark-muted)' }} onClick={() => { setSortField('county'); setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }}>
                        Location
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Area</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Docs</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Status</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Priority</th>
                      <th className="px-3 py-3 text-center text-xs font-semibold" style={{ color: 'var(--dark-muted)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => {
                      const sc = STATUS_CONFIG[p.status];
                      const docs = docCount(p.documents);
                      return (
                        <tr key={p.id} className="border-t hover:bg-white/[0.02] transition-colors" style={{ borderColor: 'var(--dark-border)' }}>
                          <td className="px-3 py-3">
                            <button onClick={() => toggleSelect(p.id)}>
                              {selected.has(p.id) ? <CheckSquare size={14} style={{ color: '#60a5fa' }} /> : <Square size={14} style={{ color: 'var(--dark-muted)' }} />}
                            </button>
                          </td>
                          <td className="px-3 py-3">
                            <div className="font-semibold text-xs" style={{ color: 'var(--dark-text)' }}>{p.owner.fullName}</div>
                            <div className="text-xs font-mono mt-0.5" style={{ color: 'var(--accent)' }}>{p.parcelId}</div>
                            <div className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{p.owner.nationalId}</div>
                          </td>
                          <td className="px-3 py-3">
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${CLASS_COLORS[p.property.propertyClass]}18`, color: CLASS_COLORS[p.property.propertyClass] }}>{p.property.propertyClass}</span>
                            {p.property.subClass && <div className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{p.property.subClass}</div>}
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{p.location.county}</div>
                            <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{p.location.district}</div>
                          </td>
                          <td className="px-3 py-3 text-right">
                            <div className="text-xs font-medium" style={{ color: 'var(--dark-text)' }}>{parseInt(p.property.areaSqm).toLocaleString()} m²</div>
                            <div className="text-xs" style={{ color: 'var(--dark-muted)' }}>{p.location.geoSource.split(' ')[0]}</div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="text-xs font-bold" style={{ color: docs >= 4 ? '#34d399' : docs >= 2 ? '#fbbf24' : '#f87171' }}>{docs}/{docTotal}</div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>{sc.label}</span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            {p.priority !== 'Normal' && (
                              <span className="text-xs px-1.5 py-0.5 rounded font-medium" style={{ backgroundColor: p.priority === 'Urgent' ? 'rgba(248,113,113,0.12)' : 'rgba(251,191,36,0.12)', color: p.priority === 'Urgent' ? '#f87171' : '#fbbf24' }}>{p.priority}</span>
                            )}
                            {p.internalFlags.length > 0 && <AlertTriangle size={12} className="inline ml-1" style={{ color: '#f87171' }} />}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button onClick={() => setDetailRecord(p)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" style={{ color: '#60a5fa' }} title="View Details">
                              <Eye size={13} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2 border-t flex items-center justify-between" style={{ borderColor: 'var(--dark-border)' }}>
                <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{filtered.length} of {properties.length} records</span>
                {selected.size > 0 && <span className="text-xs font-medium" style={{ color: 'var(--accent)' }}>{selected.size} selected</span>}
              </div>
            </div>
          </div>
        )}

        {view === 'map' && <MapView properties={filtered} />}
        {view === 'analytics' && <AnalyticsView properties={properties} />}

        {/* Modals */}
        {showWizard && <RegistrationWizard onClose={() => setShowWizard(false)} onSubmit={p => { setProperties(prev => [p, ...prev]); setShowWizard(false); }} />}
        {showBulkModal && <BulkImportModal onClose={() => setShowBulkModal(false)} />}
        {showStage2Modal && <Stage2Modal properties={properties} onClose={() => setShowStage2Modal(false)} onConfirm={handleStage2Confirm} />}
        {detailRecord && <DetailDrawer record={detailRecord} onClose={() => setDetailRecord(null)} onStatusChange={handleStatusChange} />}
      </div>
    </AppLayout>
  );
}
